import React, { forwardRef, CSSProperties, useMemo } from 'react';
import { generateTileSVG } from '../utils/tileGenerator';
import { getTileDisplayWidth, TILE_BORDER_RADIUS } from '../utils/tileLayout';

const HINT_LOCK_COLOR = '#333333';
const HINT_LOCK_OUTLINE = 'rgba(255, 255, 255, 0.6)';

/** Minimal padlock: thick shackle stroke + rounded body. */
function HintLockIcon({ size }: { size: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
      style={{ position: 'relative', zIndex: 1 }}
    >
      <path
        d="M9.5 11V8.75a2.75 2.75 0 0 1 5 0V11"
        fill="none"
        stroke={HINT_LOCK_OUTLINE}
        strokeWidth="4"
        strokeLinecap="round"
      />
      <rect
        x="7.5"
        y="11"
        width="9"
        height="8.5"
        rx="1.5"
        fill={HINT_LOCK_OUTLINE}
        stroke={HINT_LOCK_OUTLINE}
        strokeWidth="1.5"
      />
      <path
        d="M9.5 11V8.75a2.75 2.75 0 0 1 5 0V11"
        fill="none"
        stroke={HINT_LOCK_COLOR}
        strokeWidth="2.25"
        strokeLinecap="round"
      />
      <rect x="7.5" y="11" width="9" height="8.5" rx="1.5" fill={HINT_LOCK_COLOR} />
    </svg>
  );
}

interface ItemProps {
  pattern: string;
  isDragging?: boolean;
  withOpacity?: boolean;
  style?: CSSProperties;
  hintLocked?: boolean;
}

const Item = forwardRef<HTMLDivElement, ItemProps>(
  ({ isDragging, style, pattern, hintLocked = false }, ref) => {
    const borderRadius = TILE_BORDER_RADIUS;
    const width = getTileDisplayWidth();
    const renderWidth = width;

    const inlineStyles: CSSProperties = {
      height: width,
      width: width,
      borderRadius,
      cursor: isDragging ? 'grabbing' : 'grab',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      overflow: 'visible',
      boxShadow: isDragging
        ? 'rgb(63 63 68 / 5%) 0px 2px 0px 2px, rgb(34 33 81 / 15%) 0px 2px 3px 2px'
        : 'rgb(63 63 68 / 5%) 0px 0px 0px 1px, rgb(34 33 81 / 15%) 0px 1px 3px 0px',
      transform: isDragging ? 'scale(1.1)' : 'scale(1)',
      ...style,
    };

    const svgUrl = useMemo(() => {
      const svg = generateTileSVG(pattern, renderWidth);
      return `data:image/svg+xml,${encodeURIComponent(svg)}`;
    }, [pattern, renderWidth]);

    const lockIconSize = Math.round(Math.max(22, renderWidth * 0.24));

    return (
      <div ref={ref} style={inlineStyles} className='grid-item'>
        <div
          style={{
            position: 'relative',
            width: renderWidth,
            height: renderWidth,
            borderRadius,
          }}
        >
          <img
            src={svgUrl}
            width={renderWidth}
            height={renderWidth}
            style={{
              borderRadius,
              display: 'block',
              pointerEvents: 'none',
            }}
            data-pattern={pattern}
            alt="tile"
          />
          {hintLocked && (
            <div
              style={{
                position: 'absolute',
                top: 4,
                right: 4,
                pointerEvents: 'none',
              }}
            >
              <HintLockIcon size={lockIconSize} />
            </div>
          )}
        </div>
      </div>
    );
  }
);

export default Item;
